import { Link } from 'react-router-dom'
import { useLocale } from '../App'
import { DAPP_URL } from '../content'

const getStr = (c: Record<string, unknown>, key: string, def: string) => (c[key] as string | undefined) ?? def

export function AboutPage() {
  const { content: c } = useLocale()
  const cx = c as Record<string, unknown>
  const title = getStr(cx, 'aboutTitle', '关于 Equine RWA')
  const subtitle = getStr(cx, 'aboutSubtitle', '重新定义赛马资产所有权，让每个人都能拥有冠军')
  const missionTitle = getStr(cx, 'aboutMissionTitle', '我们的使命')
  const missionSubtitle = getStr(cx, 'aboutMissionSubtitle', '打造透明、公平、可信的赛马资产数字化平台')
  const missionP1 = getStr(cx, 'aboutMissionP1', 'Equine RWA 通过区块链技术将传统赛马资产数字化，让全球投资者能够以较低门槛参与这个历史悠久的资产类别。我们致力于提供真实资产背书、透明运营和基于实际表现的收益分配机制。')
  const missionP2 = getStr(cx, 'aboutMissionP2', '我们相信，通过现代科技与传统马业的结合，可以创造出一个更加开放、透明和公平的投资环境，让每个热爱赛马的人都能成为马主。')
  const valuesTitle = getStr(cx, 'aboutValuesTitle', '核心价值观')
  const teamTitle = getStr(cx, 'aboutTeamTitle', '专业团队')
  const teamSubtitle = getStr(cx, 'aboutTeamSubtitle', '融合马业专家与区块链技术人才')
  const values = (cx.aboutValues as { icon: string; title: string; desc: string }[] | undefined) ?? [
    { icon: 'fa-shield-alt', title: '透明诚信', desc: '所有交易和资产信息在区块链上公开透明，可追溯可验证，确保投资者权益。' },
    { icon: 'fa-balance-scale', title: '合规运营', desc: '严格遵守各地监管要求，建立完善的法律架构和托管机制。' },
    { icon: 'fa-users', title: '用户至上', desc: '始终将用户体验和权益放在首位，提供专业的服务和支持。' },
    { icon: 'fa-lightbulb', title: '持续创新', desc: '不断探索区块链与实物资产结合的新模式，引领行业发展。' },
  ]
  const teamCards = (cx.aboutTeamCards as { icon: string; title: string; desc: string }[] | undefined) ?? [
    { icon: 'fa-user-tie', title: '马业专家团队', desc: '30年以上赛马行业经验' },
    { icon: 'fa-laptop-code', title: '技术开发团队', desc: '顶尖区块链开发专家' },
    { icon: 'fa-gavel', title: '法律合规团队', desc: '资深金融法律顾问' },
    { icon: 'fa-headset', title: '客户服务团队', desc: '7x24小时专业支持' },
  ]

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="header-content">
            <h1 className="page-title">{title}</h1>
            <p className="page-subtitle">{subtitle}</p>
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', background: 'var(--color-white)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{missionTitle}</h2>
            <p className="section-subtitle">{missionSubtitle}</p>
          </div>
          <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
            <p style={{ fontSize: '1.15rem', lineHeight: 1.8, color: 'var(--color-gray-700)', marginBottom: '2rem' }}>{missionP1}</p>
            <p style={{ fontSize: '1.15rem', lineHeight: 1.8, color: 'var(--color-gray-700)' }}>{missionP2}</p>
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', background: 'var(--color-background)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{valuesTitle}</h2>
          </div>
          <div className="features-grid about-values-grid">
            {values.map((v: { icon: string; title: string; desc: string }, i: number) => (
              <div key={i} className="feature-card">
                <div className="feature-icon">
                  <i className={`fas ${v.icon}`}></i>
                </div>
                <h3 className="feature-title">{v.title}</h3>
                <p className="feature-description">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '6rem 0', background: 'var(--color-white)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{teamTitle}</h2>
            <p className="section-subtitle">{teamSubtitle}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {teamCards.map((t: { icon: string; title: string; desc: string }, i: number) => (
              <div key={i} style={{ textAlign: 'center', padding: '2rem', background: 'var(--color-background)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
                <div style={{ width: 100, height: 100, margin: '0 auto 1rem', background: 'var(--color-primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2.5rem' }}>
                  <i className={`fas ${t.icon}`}></i>
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-secondary)', marginBottom: '0.5rem' }}>{t.title}</h3>
                <p style={{ color: 'var(--color-gray-600)' }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{getStr(cx, 'aboutCtaTitle', '加入 Equine RWA 社区')}</h2>
            <p className="cta-subtitle">{getStr(cx, 'aboutCtaSubtitle', '与全球马主一起，见证冠军的诞生')}</p>
            <div className="cta-buttons">
              <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-large btn-primary">
                <i className="fas fa-rocket"></i> {(c as { ctaButton?: string }).ctaButton ?? '进入dapp'}
              </a>
              <Link to="/contact" className="btn btn-large btn-secondary">
                <i className="fas fa-envelope"></i> {c.footerContactTitle ?? '联系我们'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
